/**
 * CrmSendButton: "Envoyer au CRM" in the post-call sheet (2026-09-22).
 *
 * Shown under the qualification box of one call once that call has an
 * outcome. The server decides which CRM the client of this prospect uses;
 * this component only reflects the answer:
 *   none / not_configured  button disabled, the reason names the client
 *   not_sent               button enabled, labelled with the target CRM
 *   pending                waiting for the outbox worker (polled every 5 s)
 *   delivered              "Envoyé le ..." with the date
 *   failed / dead          the reason, and a "Réessayer" button
 * A 409 on send (no CRM, call not qualified) is shown as the reason.
 */
import { useEffect, useState } from 'react';
import { HUBSPOT_UI_ENABLED } from '@/lib/hubspotUi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Loader2, RotateCcw, Send } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ApiErrorException } from '@/api/client';
import { getCallCrmStatus, sendCallToCrm, type CrmSendResult } from '@/api/endpoints/crm';

export const crmSendStatusKey = (callId: string) => ['crm-send-status', callId] as const;

interface CrmSendButtonProps {
  callId: string;
  /** The call has an outcome (or a meeting): only then is there something to send. */
  qualified: boolean;
}

function providerName(provider: CrmSendResult['provider']): string {
  return provider === 'hubspot' ? 'HubSpot' : provider === 'swoft' ? 'Swoft' : '';
}

export default function CrmSendButton({ callId, qualified }: CrmSendButtonProps) {
  const { t } = useTranslation(['dialer']);
  const queryClient = useQueryClient();
  const [refusal, setRefusal] = useState<string | null>(null);
  // The outbox worker delivers within seconds: poll while a row is pending.
  const [polling, setPolling] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: crmSendStatusKey(callId),
    queryFn: ({ signal }) => getCallCrmStatus(callId, { signal }),
    enabled: qualified && !!callId,
    staleTime: 10_000,
    refetchInterval: polling ? 5_000 : false,
  });

  useEffect(() => {
    setPolling(data?.status === 'pending');
  }, [data?.status]);

  const send = useMutation({
    mutationFn: () => sendCallToCrm(callId),
    onSuccess: (result) => {
      setRefusal(null);
      queryClient.setQueryData(crmSendStatusKey(callId), result);
      if (result.status === 'delivered') toast.success(t('dialer:crmSend.toastSent', { provider: providerName(result.provider) }));
      else if (result.status === 'pending') toast.success(t('dialer:crmSend.toastQueued', { provider: providerName(result.provider) }));
      else if (result.status === 'failed' || result.status === 'dead') toast.error(t('dialer:crmSend.failed', { reason: result.lastError ?? '' }));
    },
    onError: (err: unknown) => {
      const message = err instanceof ApiErrorException && err.status === 409 ? err.message : t('dialer:crmSend.error');
      setRefusal(message);
      toast.error(message);
    },
  });

  if (!qualified) return null;

  const provider = data?.provider ?? 'none';
  // HubSpot hidden from the UI (AUDIT-INT-08, src/lib/hubspotUi.ts): no
  // "Send to HubSpot" button and no HubSpot status line. The server side of
  // the CRM send is unchanged.
  if (provider === 'hubspot' && !HUBSPOT_UI_ENABLED) return null;
  const name = providerName(provider);
  const customer = data?.customerName || '';
  const sendLabel = provider === 'hubspot'
    ? t('dialer:crmSend.sendToHubspot')
    : provider === 'swoft'
      ? t('dialer:crmSend.sendToSwoft')
      : t('dialer:crmSend.send');

  let reason: string | null = refusal;
  if (!reason && data) {
    if (provider === 'none') reason = customer ? t('dialer:crmSend.noCrm', { customer }) : t('dialer:crmSend.noCrmUnknown');
    else if (data.status === 'not_configured') reason = t('dialer:crmSend.notConfigured', { provider: name, customer: customer || t('dialer:crmSend.thisClient') });
  }
  const disabled = isLoading || !data || provider === 'none' || data.status === 'not_configured' || send.isPending;

  const stopClick = (e: React.MouseEvent) => e.stopPropagation();

  if (data?.status === 'delivered') {
    return (
      <div className="mb-2 flex items-center gap-2 text-xs text-emerald-700" onClick={stopClick}>
        <Check className="w-3.5 h-3.5" />
        <span>{t('dialer:crmSend.sentAt', { provider: name, date: data.sentAt ? moment(data.sentAt).format('DD/MM/YYYY HH:mm') : '' })}</span>
      </div>
    );
  }

  if (data?.status === 'pending') {
    return (
      <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground" onClick={stopClick}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>{t('dialer:crmSend.pending', { provider: name })}</span>
      </div>
    );
  }

  if (data?.status === 'failed' || data?.status === 'dead') {
    return (
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs" onClick={stopClick}>
        <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
        <span className="text-rose-700">{t('dialer:crmSend.failed', { reason: data.lastError || '' })}</span>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => send.mutate()} disabled={send.isPending}>
          {send.isPending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
          {t('dialer:crmSend.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-2 flex flex-wrap items-center gap-2" onClick={stopClick}>
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs"
        onClick={() => send.mutate()}
        disabled={disabled}
        title={reason ?? undefined}
      >
        {send.isPending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
        {send.isPending ? t('dialer:crmSend.sending') : sendLabel}
      </Button>
      {reason && <span className="text-[11px] text-muted-foreground">{reason}</span>}
    </div>
  );
}
